import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Gift, Loader2, Sun, Home, Search, ArrowRightLeft, Filter, Plus, X, Globe, Radio } from "lucide-react";

const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return "—"; } };

export default function FreeUsers() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tool, setTool] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [moving, setMoving] = useState(null);
  const [exclusions, setExclusions] = useState([]);
  const [term, setTerm] = useState("");
  const [exBusy, setExBusy] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "partner";

  const moveToLead = async (id) => {
    setMoving(id);
    try {
      const { data } = await api.post(`/free-users/${id}/to-lead`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      setConfirmId(null);
      toast.success("Contatto spostato nei Lead", { action: { label: "Apri", onClick: () => navigate(`/leads/${data.lead_id}`) } });
    } catch (e) {
      toast.error("Errore nello spostamento in Lead");
    } finally {
      setMoving(null);
    }
  };

  const load = () => api.get("/free-users").then((r) => { setItems(r.data.items || []); setStats(r.data.stats || null); }).catch(() => {}).finally(() => setLoading(false));
  const loadExclusions = () => api.get("/free-users/exclusions").then((r) => setExclusions(r.data.items || [])).catch(() => {});

  useEffect(() => { load(); if (canManage) loadExclusions(); }, [canManage]);

  const addTerm = async () => {
    const t = term.trim();
    if (!t) return;
    setExBusy(true);
    try {
      await api.post("/free-users/exclusions", { term: t });
      setTerm("");
      await loadExclusions();
      setLoading(true); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiunta");
    } finally { setExBusy(false); }
  };

  const removeTerm = async (id) => {
    setExBusy(true);
    try {
      await api.delete(`/free-users/exclusions/${id}`);
      await loadExclusions();
      setLoading(true); await load();
    } catch {
      toast.error("Errore nella rimozione");
    } finally { setExBusy(false); }
  };

  const filtered = items.filter((i) => {
    if (tool && i.tool !== tool) return false;
    const t = q.trim().toLowerCase();
    if (t && !(`${i.name} ${i.email} ${i.comune} ${i.provincia}`.toLowerCase().includes(t))) return false;
    return true;
  });

  return (
    <div className="p-6 space-y-6" data-testid="free-users-page">
      <div className="flex items-center gap-3">
        <Gift className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-xl font-bold leading-none">Area Free</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Utenti che hanno usato gli strumenti gratuiti sul sito (bolletta / tetto)</p>
        </div>
        <Badge variant="secondary" className="ml-auto" data-testid="free-users-count">{stats?.total ?? items.length} consulenze</Badge>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3" data-testid="free-kpis">
          <Card className="p-4" data-testid="kpi-total">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-xs text-muted-foreground mt-1">Consulenze totali</div>
          </Card>
          <Card className="p-4" data-testid="kpi-pct">
            <div className="text-2xl font-bold text-primary">{stats.pct_lead}%</div>
            <div className="text-xs text-muted-foreground mt-1">Hanno generato lead utilizzabile</div>
          </Card>
        </div>
      )}

      {canManage && (
      <Card className="p-4 space-y-3" data-testid="free-exclusions">
        <div className="flex items-start gap-2">
          <Filter className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div>
            <div className="text-sm font-semibold leading-none">Esclusioni</div>
            <p className="text-xs text-muted-foreground mt-1">Parole, email o info: i contatti associati a un termine non vengono conteggiati né mostrati.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Input value={term} onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTerm(); } }}
            placeholder="Aggiungi parola, email o info…" data-testid="exclusion-input" />
          <button onClick={addTerm} disabled={exBusy || !term.trim()}
            className="px-3 py-2 rounded bg-primary text-primary-foreground font-medium inline-flex items-center gap-1 disabled:opacity-60 whitespace-nowrap"
            data-testid="exclusion-add"><Plus className="h-4 w-4" /> Aggiungi</button>
        </div>
        {exclusions.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="exclusion-list">
            {exclusions.map((e) => (
              <span key={e.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/40 text-xs px-2.5 py-1" data-testid={`exclusion-chip-${e.id}`}>
                {e.term}
                <button onClick={() => removeTerm(e.id)} disabled={exBusy} className="text-muted-foreground hover:text-destructive disabled:opacity-50" data-testid={`exclusion-remove-${e.id}`}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
      </Card>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex items-center flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email, comune…" className="pl-9" data-testid="free-users-search" />
        </div>
        <div className="flex gap-2">
          {[["", "Tutti"], ["bolletta", "Bolletta"], ["tetto", "Tetto"]].map(([v, l]) => (
            <button key={v} onClick={() => setTool(v)} data-testid={`free-filter-${v || "all"}`}
              className={`px-3 py-2 rounded text-sm font-medium border transition-colors ${tool === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground rounded border-border" data-testid="free-users-empty">Nessun utente free trovato.</Card>
      ) : (
        <Card className="rounded border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-muted-foreground text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Strumento</th>
                  <th className="text-left px-4 py-3 font-semibold">Nominativo</th>
                  <th className="text-left px-4 py-3 font-semibold">Professione</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Cellulare</th>
                  <th className="text-left px-4 py-3 font-semibold">Indirizzo</th>
                  <th className="text-left px-4 py-3 font-semibold">Località</th>
                  <th className="text-left px-4 py-3 font-semibold">Partner</th>
                  <th className="text-left px-4 py-3 font-semibold">Dimensionamento</th>
                  <th className="text-left px-4 py-3 font-semibold">Data</th>
                  <th className="text-right px-4 py-3 font-semibold">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} className="border-t border-border hover:bg-secondary/20" data-testid={`free-user-row-${i.id}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                        {i.tool === "tetto" ? <Home className="h-3.5 w-3.5 text-primary" /> : <Sun className="h-3.5 w-3.5 text-primary" />}
                        {i.tool === "tetto" ? "Tetto" : "Bolletta"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium"><span className="flex items-center gap-2">{i.registered ? i.name : <span className="text-muted-foreground italic">Non registrato</span>}<OriginBadge it={i} /></span></td>
                    <td className="px-4 py-3">{i.professione || "—"}</td>
                    <td className="px-4 py-3">{i.email ? <a href={`mailto:${i.email}`} className="text-primary hover:underline">{i.email}</a> : "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{i.phone ? <a href={`tel:${i.phone}`} className="text-primary hover:underline">{i.phone}</a> : "—"}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate" title={i.address || ""}>{i.address || "—"}</td>
                    <td className="px-4 py-3">{[i.comune, i.provincia].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-4 py-3">{i.partner_name ? <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-600 text-[11px] font-medium px-2 py-0.5">{i.partner_name}</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-xs">
                      {i.tool === "tetto"
                        ? (i.roof_kwp != null ? `${i.roof_panels} pannelli · ${Number(i.roof_kwp).toLocaleString("it-IT", { maximumFractionDigits: 1 })} kWp` : "—")
                        : (i.pv_kwp != null ? `${Number(i.pv_kwp).toLocaleString("it-IT", { maximumFractionDigits: 1 })} kWp · ${i.consumo_annuo_kwh ? Number(i.consumo_annuo_kwh).toLocaleString("it-IT") + " kWh" : "—"}` : "—")}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(i.created_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {confirmId === i.id ? (
                        <span className="inline-flex items-center gap-1">
                          <button onClick={() => moveToLead(i.id)} disabled={moving === i.id} className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground font-medium disabled:opacity-60" data-testid={`free-move-confirm-${i.id}`}>{moving === i.id ? "…" : "Conferma"}</button>
                          <button onClick={() => setConfirmId(null)} disabled={moving === i.id} className="text-xs px-2 py-1 rounded border border-border" data-testid={`free-move-cancel-${i.id}`}>Annulla</button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmId(i.id)} className="text-xs px-2 py-1 rounded border border-primary text-primary hover:bg-primary/10 inline-flex items-center gap-1.5 font-medium" data-testid={`free-move-${i.id}`}><ArrowRightLeft className="h-3.5 w-3.5" /> {(i.origin_channel === "rete" || i.origin_agent) ? "Sblocca e lavora" : "Sposta in Lead"}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function OriginBadge({ it }) {
  const isRete = it.origin_channel === "rete" || !!it.origin_agent;
  if (isRete) {
    return <span className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`free-origin-${it.id}`} title="Configurazione avviata dalla rete (rete.torri.solar)"><Radio className="h-3 w-3" />Rete{it.origin_agent ? ` · ${it.origin_agent}` : ""}</span>;
  }
  if (it.created_by_name === "Self-service (web)") {
    return <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`free-origin-${it.id}`} title="Lead self-service (web pubblico)"><Globe className="h-3 w-3" />Self-service</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-600 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`free-origin-${it.id}`} title="Lead da strumento Free (web)"><Globe className="h-3 w-3" />Free web</span>;
}
